import { Link, useParams } from "react-router-dom";
import PageBanner from "@/components/feature/PageBanner";
import { useApi } from "@/hooks/useApi";
import type { DocItem } from "@/lib/types";
import { Skeleton } from "@/components/base/Skeleton";
import EmptyState from "@/components/base/EmptyState";
import RuleBody from "@/components/feature/rules/RuleBody";
import { formatRevdate } from "@/components/feature/rules/RuleCard";

// /rules/:code — 규정 전문 (code = rule | bylaw | mutual)
export default function RuleDetailPage() {
  const { code = "" } = useParams();
  const { data, loading, error, reload } = useApi<DocItem>(
    code ? `docs.php?code=${encodeURIComponent(code)}` : null
  );
  const { data: list } = useApi<DocItem[]>("docs.php");

  const docs = list ?? [];
  const ready = Number(data?.has ?? 1) !== 0;
  const rev = formatRevdate(data?.revdate);

  return (
    <div className="rule-page">
      <PageBanner
        title={data?.name || "규정"}
        breadcrumb={[
          { label: "규정", to: "/rules" },
          { label: data?.name || "전문" },
        ]}
      />

      <section className="mx-auto max-w-6xl px-4 py-10 md:px-6 md:py-14">
        {/* 개정일 + 인쇄 */}
        <div className="mb-8 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-foreground-500">{rev}</p>
          <button
            type="button"
            onClick={() => window.print()}
            className="no-print inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-semibold text-foreground-700 transition-colors hover:bg-background-100"
          >
            <i className="ri-printer-line" aria-hidden="true"></i>
            인쇄
          </button>
        </div>

        {loading ? (
          <div className="max-w-3xl space-y-3">
            {Array.from({ length: 9 }).map((_, i) => (
              <Skeleton key={i} className="h-4 w-full" />
            ))}
          </div>
        ) : error ? (
          <EmptyState
            icon="ri-error-warning-line"
            title="규정을 불러오지 못했습니다"
            description={error}
            action={
              <button
                type="button"
                onClick={reload}
                className="whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                다시 시도
              </button>
            }
          />
        ) : !ready ? (
          <EmptyState
            icon="ri-draft-line"
            title="준비 중인 규정입니다"
            description="내용을 준비하고 있습니다."
          />
        ) : (
          <RuleBody content={String(data?.content || "")} />
        )}

        {/* 다른 규정 링크 */}
        {docs.length > 0 && (
          <nav
            aria-label="다른 규정"
            className="no-print mt-12 border-t border-background-200 pt-6"
          >
            <h3 className="mb-3 font-heading text-sm font-bold text-foreground-800">
              다른 규정
            </h3>
            <div className="flex flex-wrap gap-2">
              {docs.map((d) => (
                <Link
                  key={d.code}
                  to={`/rules/${d.code}`}
                  className={`whitespace-nowrap rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
                    d.code === code
                      ? "border-primary-300 bg-primary-50 text-primary-700"
                      : "border-background-200 bg-background-50 text-foreground-700 hover:bg-background-100"
                  }`}
                >
                  {d.name}
                </Link>
              ))}
            </div>
          </nav>
        )}
      </section>
    </div>
  );
}